// Phase B — Friends + Private DMs hub.
// Left: tabs (Threads / Friends / Requests / Find people)
// Right: open conversation (shown inline on desktop, routed on mobile)

import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DashboardLayout } from "@/pages/Dashboard";
import { useLanguage, useAuth } from "@/App";
import { communityApi } from "@/lib/community";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import ChatSurface from "@/components/community/ChatSurface";
import BackBar from "@/components/BackBar";
import EmptyState from "@/components/EmptyState";
import {
  Search, UserPlus, MessageSquare, Check, X, Loader2, Users, Inbox,
  UserMinus, Ban,
} from "lucide-react";

const TABS = [
  { id: "threads",  ar: "المحادثات",  en: "Chats",    Icon: MessageSquare },
  { id: "friends",  ar: "الأصدقاء",   en: "Friends",  Icon: Users },
  { id: "requests", ar: "الطلبات",    en: "Requests", Icon: Inbox },
  { id: "search",   ar: "ابحث عن أصدقاء", en: "Find people", Icon: UserPlus },
];

export default function Messages() {
  const { t, language } = useLanguage();
  const { token } = useAuth();
  const navigate = useNavigate();
  const { tid } = useParams();
  const [tab, setTab] = useState("threads");
  const [threads, setThreads]   = useState([]);
  const [friends, setFriends]   = useState([]);
  const [requests, setRequests] = useState([]);
  const [searchQ, setSearchQ]   = useState("");
  const [searchResults, setSR]  = useState([]);
  const [loading, setLoading]   = useState(true);

  const refresh = useCallback(async () => {
    try {
      const [thr, fr, rq] = await Promise.all([
        communityApi.dmThreads(token),
        communityApi.myFriends(token),
        communityApi.myRequests(token),
      ]);
      setThreads(thr); setFriends(fr); setRequests(rq);
    } catch { /* polling will retry */ }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => {
    if (!token) { navigate("/login"); return; }
    let mounted = true;
    (async () => {
      try {
        const [thr, fr, rq] = await Promise.all([
          communityApi.dmThreads(token),
          communityApi.myFriends(token),
          communityApi.myRequests(token),
        ]);
        if (!mounted) return;
        setThreads(thr); setFriends(fr); setRequests(rq);
      } catch { /* silent */ }
      finally { if (mounted) setLoading(false); }
    })();
    const iv = setInterval(refresh, 8000);
    return () => { mounted = false; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate]);

  const doSearch = async (q) => {
    setSearchQ(q);
    if (!q.trim()) { setSR([]); return; }
    try { setSR(await communityApi.searchUsers(q, token)); } catch { setSR([]); }
  };

  const sendRequest = async (u) => {
    try {
      await communityApi.requestFriend(u.user_id, token);
      toast.success(t("تم إرسال طلب الصداقة", "Friend request sent"));
      doSearch(searchQ);  // refresh status
    } catch { toast.error(t("تعذّر إرسال الطلب", "Could not send request")); }
  };

  const act = async (uid, action, okMsg) => {
    try { await communityApi.friendAction(uid, action, token); toast.success(okMsg); refresh(); }
    catch { toast.error(t("فشلت العملية", "Action failed")); }
  };

  const openDM = async (userId) => {
    try {
      const { thread_id } = await communityApi.dmStart(userId, token);
      navigate(`/messages/${thread_id}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || t("تعذّر فتح المحادثة", "Could not open chat");
      toast.error(msg);
    }
  };

  // If a thread is selected on a mobile route, render only the chat.
  if (tid) {
    const thread = threads.find((x) => x.thread_id === tid);
    return (
      <DashboardLayout title={thread?.other_name || t("محادثة", "Chat")}>
        <BackBar to="/messages" label_ar="الرسائل والأصدقاء" label_en="Messages & Friends"
          crumbs={[
            { to: "/dashboard", ar: "الرئيسية", en: "Home" },
            { to: "/messages", ar: "الرسائل", en: "Messages" },
            { ar: thread?.other_name || "محادثة", en: thread?.other_name || "Chat" },
          ]} />
        <ChatSurface slug={tid} mode="dm" header={thread} />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={t("الرسائل والأصدقاء", "Messages & Friends")}>
      <div className="space-y-4" data-testid="messages-page">
        {/* Tabs */}
        <div className="glass-card rounded-2xl p-2 inline-flex flex-wrap gap-1" data-testid="messages-tabs">
          {TABS.map((tt) => (
            <button key={tt.id}
              onClick={() => setTab(tt.id)}
              className={`text-sm px-3 py-2 rounded-xl inline-flex items-center gap-2 transition ${tab === tt.id ? "bg-yellow-500 text-black font-semibold" : "text-slate-300 hover:bg-slate-800"}`}
              data-testid={`messages-tab-${tt.id}`}>
              <tt.Icon size={14} />{language === "ar" ? tt.ar : tt.en}
              {tt.id === "requests" && requests.length > 0 && <Badge className="bg-red-500 text-white text-[10px]">{requests.length}</Badge>}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="glass-card rounded-2xl p-10 flex items-center justify-center"><Loader2 className="animate-spin text-yellow-500" /></div>
        ) : (
          <div className="glass-card rounded-2xl p-3 lg:p-4">
            {tab === "threads" && (
              threads.length === 0 ? (
                <EmptyState
                  icon={MessageSquare}
                  title_ar="صندوق الرسائل فارغ"
                  title_en="Inbox is empty"
                  body_ar="ابدأ محادثة جديدة أو ابحث عن أصدقاء جدد لتبدأ بالتواصل."
                  body_en="Start a new chat or find friends to connect with."
                  cta_ar="ابحث عن أصدقاء"
                  cta_en="Find friends"
                  onCta={() => setTab("search")}
                  secondaryCtaAr="انتقل إلى المجتمع"
                  secondaryCtaEn="Browse community"
                  onSecondary={() => navigate("/community")}
                  testid="messages-threads-empty"
                />
              ) : threads.map((th) => (
                <button key={th.thread_id} onClick={() => navigate(`/messages/${th.thread_id}`)}
                  className="w-full text-right flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-800/60 transition" data-testid={`dm-thread-${th.thread_id}`}>
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center text-white font-bold flex-shrink-0">{(th.other_name || "?").slice(0,1).toUpperCase()}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-white truncate">{th.other_name || "—"}</span>
                      <span className="text-[10px] text-slate-500">{th.last_message_at ? new Date(th.last_message_at).toLocaleTimeString(language === "ar" ? "ar-EG" : "en-US", { hour:"2-digit", minute:"2-digit" }) : ""}</span>
                    </div>
                    <div className="text-xs text-slate-400 truncate">{th.last_message_preview || t("لا توجد رسائل", "No messages")}</div>
                  </div>
                  {th.unread_count > 0 && <Badge className="bg-emerald-500 text-white">{th.unread_count}</Badge>}
                </button>
              ))
            )}

            {tab === "friends" && (
              friends.length === 0 ? (
                <div className="text-center text-slate-400 py-10 text-sm">{t("ليس لديك أصدقاء بعد", "No friends yet")}</div>
              ) : friends.map((f) => (
                <div key={f.user_id} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-800/40" data-testid={`friend-${f.user_id}`}>
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold">{(f.name || "?").slice(0,1).toUpperCase()}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-white">{f.name}</div>
                    <div className="text-xs text-slate-400">{f.city ? `${f.city}${f.state ? ", " + f.state : ""}` : (f.username ? "@" + f.username : "")}</div>
                  </div>
                  <Button size="sm" onClick={() => openDM(f.user_id)} className="btn-primary" data-testid={`friend-message-${f.user_id}`}>
                    <MessageSquare size={12} className="ml-1" />{t("راسلة", "Message")}
                  </Button>
                  <button onClick={() => act(f.user_id, "remove", t("تم حذف الصديق", "Friend removed"))} className="text-slate-400 hover:text-red-400 p-1.5" title={t("حذف", "Remove")} data-testid={`friend-remove-${f.user_id}`}><UserMinus size={14} /></button>
                  <button onClick={() => act(f.user_id, "block", t("تم الحظر", "Blocked"))} className="text-slate-400 hover:text-red-400 p-1.5" title={t("حظر", "Block")} data-testid={`friend-block-${f.user_id}`}><Ban size={14} /></button>
                </div>
              ))
            )}

            {tab === "requests" && (
              requests.length === 0 ? (
                <div className="text-center text-slate-400 py-10 text-sm">{t("لا توجد طلبات صداقة", "No friend requests")}</div>
              ) : requests.map((r) => (
                <div key={r.user_id} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-800/40" data-testid={`request-${r.user_id}`}>
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white font-bold">{(r.name || "?").slice(0,1).toUpperCase()}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-white">{r.name}</div>
                    <div className="text-xs text-slate-400">{t("يريد إضافتك صديقاً", "wants to be your friend")}</div>
                  </div>
                  <Button size="sm" onClick={() => act(r.user_id, "accept", t("تمت إضافته", "Accepted"))} className="btn-primary" data-testid={`request-accept-${r.user_id}`}>
                    <Check size={14} />
                  </Button>
                  <button onClick={() => act(r.user_id, "reject", t("تم الرفض", "Rejected"))} className="text-slate-300 hover:text-red-400 p-1.5" data-testid={`request-reject-${r.user_id}`}><X size={14} /></button>
                </div>
              ))
            )}

            {tab === "search" && (
              <div className="space-y-3">
                <div className="relative">
                  <Search size={14} className="absolute right-3 top-3 text-slate-500" />
                  <Input value={searchQ} onChange={(e) => doSearch(e.target.value)}
                    placeholder={t("ابحث بالاسم أو البريد الإلكتروني…", "Search by name or email…")}
                    className="bg-slate-900 text-white border-slate-700 pr-9"
                    data-testid="user-search-input" />
                </div>
                {searchResults.length === 0 && searchQ.trim() && (
                  <div className="text-center text-slate-500 py-6 text-sm">{t("لا توجد نتائج", "No results")}</div>
                )}
                {searchResults.map((u) => (
                  <div key={u.user_id} className="flex items-center gap-3 p-2.5 rounded-xl bg-slate-900/40 border border-slate-800" data-testid={`user-result-${u.user_id}`}>
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-600 to-slate-800 flex items-center justify-center text-white font-bold">{(u.name || "?").slice(0,1).toUpperCase()}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-white">{u.name}</div>
                      <div className="text-xs text-slate-400">{u.username ? `@${u.username}` : ""} {u.city ? `· ${u.city}${u.state ? ", " + u.state : ""}` : ""}</div>
                    </div>
                    {u.friend_status === "accepted" ? (
                      <Button size="sm" onClick={() => openDM(u.user_id)} className="btn-primary" data-testid={`user-message-${u.user_id}`}>
                        <MessageSquare size={12} className="ml-1" />{t("راسله", "Message")}
                      </Button>
                    ) : u.friend_status === "pending_out" ? (
                      <Badge className="bg-slate-800 text-slate-300">{t("في الانتظار", "Pending")}</Badge>
                    ) : u.friend_status === "pending_in" ? (
                      <Button size="sm" onClick={() => act(u.user_id, "accept", t("تمت الإضافة", "Accepted"))} className="btn-primary" data-testid={`user-accept-${u.user_id}`}>
                        <Check size={12} className="ml-1" />{t("قبول", "Accept")}
                      </Button>
                    ) : u.friend_status === "blocked" ? (
                      <Badge className="bg-red-500/20 text-red-300">{t("محظور", "Blocked")}</Badge>
                    ) : (
                      <Button size="sm" onClick={() => sendRequest(u)} className="btn-primary" data-testid={`user-add-${u.user_id}`}>
                        <UserPlus size={12} className="ml-1" />{t("أضف", "Add friend")}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
